import { Eye, GitBranch, Clock, Target, TrendingUp, Activity, ChevronDown, ChevronUp, Zap, MousePointer, FileDown, ArrowRight, ArrowDown } from 'lucide-react';
import { useDeceptions, useDeceptionInteractions, useDashboardStats } from '../lib/hooks';
import { useAuth } from '../lib/auth-context';
import { useState, useMemo } from 'react';
import type { DeceptionInteraction } from '../lib/api';
import { safeDate, safeDuration, safeNumber } from '../lib/validation';
import { EmptyState } from '../components/EmptyState';
import { MetricCardSkeleton } from '../components/SkeletonLoader';
import {
  ENGAGEMENT_VOCAB,
  isDeepCallback,
  isTimeBurned,
  resolveDeepCallbacks,
  timeBurnRate,
} from '../lib/engagement-vocab';

interface PathNode {
  path: string;
  sessionId: string;
  /** Deep callback (engaged flag) */
  deepCallback: boolean;
  /** Applied any delay */
  timeBurned: boolean;
  timeWasted: number;
  depth: number;
  variant?: string;
  createdAt: string;
}

export function Deceptions() {
  const { isConnected } = useAuth();
  const { data, isLoading } = useDeceptions();
  const { data: interactionsData, isLoading: interactionsLoading } = useDeceptionInteractions();
  const { data: stats } = useDashboardStats();
  const [activeTab, setActiveTab] = useState<'overview' | 'paths' | 'interactions'>('overview');

  const deceptions = data?.deceptions ?? [];
  const totalDeceptions = deceptions.length;
  /** Time burned: any decoy that delayed the client */
  const timeBurnedCount = deceptions.filter((d) => isTimeBurned(d.time_wasted_ms)).length;
  /**
   * Deep callbacks come from the stats endpoint (engagement_hops), NOT from
   * counting the per-row `engaged` flag — the July ops-chain fast path does not
   * set that flag, so a client-side count of it undercounts.
   * See docs/research/B1_DEEP_CALLBACK_PROBE.md.
   */
  const deepCallbacks = resolveDeepCallbacks(stats);
  const totalTimeWasted = deceptions.reduce((sum, d) => {
    const time = safeNumber(d.time_wasted_ms);
    return sum + time;
  }, 0);

  // Average of the MAX depth reached per session (robust to missing/invalid values)
  const avgPathDepth = useMemo(() => {
    if (!deceptions || deceptions.length === 0) return 0;
    const perSessionMax = new Map<string, number>();
    for (const d of deceptions) {
      const sid = d.session_id ?? 'unknown';
      const depth = Number(d.breadcrumbs_followed ?? 0) || 0;
      const prev = perSessionMax.get(sid) ?? 0;
      if (depth > prev) perSessionMax.set(sid, depth);
    }
    const depths = Array.from(perSessionMax.values());
    if (depths.length === 0) return 0;
    const sum = depths.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
    return sum / depths.length;
  }, [deceptions]);

  // Variant color mapping for quick differentiation
  const variantColors: Record<string, string> = useMemo(() => ({
    'admin-panel': 'bg-sky-500/20 text-sky-400 border border-sky-500/30',
    'api-endpoint': 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30',
    'env-file': 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    'directory-listing': 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
    'login-form': 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30',
    'database-dump': 'bg-rose-500/20 text-rose-400 border border-rose-500/30',
  }), []);

  // Track which sessions are expanded (default collapsed)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggleExpanded = (sid: string) => setExpanded((e) => ({ ...e, [sid]: !(e[sid] ?? false) }));

  // Deterministic session color rail
  const sessionHue = (sid: string) => {
    let h = 0; for (let i = 0; i < (sid?.length ?? 0); i++) h = (h * 31 + sid.charCodeAt(i)) % 360;
    return `hsl(${h}, 70%, 45%)`;
  };

  // Group deceptions by session to build path trees
  const sessionPaths = new Map<string, PathNode[]>();
  deceptions.forEach(d => {
    if (!sessionPaths.has(d.session_id)) {
      sessionPaths.set(d.session_id, []);
    }
    sessionPaths.get(d.session_id)!.push({
      path: d.path,
      sessionId: d.session_id,
      deepCallback: isDeepCallback(d.engaged),
      timeBurned: isTimeBurned(d.time_wasted_ms),
      timeWasted: d.time_wasted_ms,
      depth: d.breadcrumbs_followed,
      variant: d.variant || undefined,
      createdAt: d.created_at
    });
  });

  // Sort paths by timestamp to show progression
  sessionPaths.forEach(paths => {
    paths.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[100vw] overflow-x-hidden">
      <div className="mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Deception Analytics</h2>
        <p className="text-gray-400 text-sm sm:text-base">
          {ENGAGEMENT_VOCAB.timeBurned.short} = decoy delay. {ENGAGEMENT_VOCAB.deepCallbacks.short} =
          attacker followed a breadcrumb.
        </p>
      </div>

      {/* Deception funnel */}
      <div className="bg-sg-card border border-sg-border rounded-xl p-6 mb-8">
        <div className="text-sm text-gray-400 mb-4 font-medium">DECEPTION FUNNEL</div>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex-1 text-center w-full sm:w-auto">
            <div className="text-3xl font-bold text-white">{interactionsLoading ? '—' : (interactionsData?.funnel?.total_served ?? totalDeceptions)}</div>
            <div className="text-sm text-gray-400 mt-1">{ENGAGEMENT_VOCAB.decoysServed.short}</div>
          </div>
          <ArrowRight className="w-6 h-6 text-gray-600 hidden sm:block shrink-0" aria-hidden />
          <ArrowDown className="w-6 h-6 text-gray-600 sm:hidden shrink-0" aria-hidden />
          <div className="flex-1 text-center w-full sm:w-auto">
            <div className="text-3xl font-bold text-blue-400">{interactionsLoading ? '—' : (interactionsData?.funnel?.total_accessed ?? 0)}</div>
            <div className="text-sm text-gray-400 mt-1">Accessed</div>
            {!interactionsLoading && interactionsData?.funnel && interactionsData.funnel.total_served > 0 && (
              <div className="text-xs text-blue-400 mt-1">
                {((interactionsData.funnel.total_accessed / interactionsData.funnel.total_served) * 100).toFixed(1)}%
              </div>
            )}
          </div>
          <ArrowRight className="w-6 h-6 text-gray-600 hidden sm:block shrink-0" aria-hidden />
          <ArrowDown className="w-6 h-6 text-gray-600 sm:hidden shrink-0" aria-hidden />
          <div className="flex-1 text-center w-full sm:w-auto">
            <div className="text-3xl font-bold text-sg-green">{interactionsLoading ? '—' : (interactionsData?.funnel?.total_interacted ?? 0)}</div>
            <div className="text-sm text-gray-400 mt-1">Interacted</div>
            {!interactionsLoading && interactionsData?.funnel && interactionsData.funnel.total_accessed > 0 && (
              <div className="text-xs text-sg-green mt-1">
                {((interactionsData.funnel.total_interacted / interactionsData.funnel.total_accessed) * 100).toFixed(1)}%
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-8">
        {isLoading ? (
          <>
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
          </>
        ) : (
          <>
            <div className="bg-sg-card border border-sg-border rounded-xl p-6 transition-all hover:border-blue-500/30">
              <div className="text-sm text-gray-400 mb-2">Total Deceptions</div>
              <div className="text-3xl font-bold text-white">{totalDeceptions}</div>
            </div>
            <div
              className="bg-sg-card border border-sg-border rounded-xl p-6 transition-all hover:border-amber-500/30"
              title={ENGAGEMENT_VOCAB.timeBurned.hint}
            >
              <div className="text-sm text-gray-400 mb-2">{ENGAGEMENT_VOCAB.timeBurned.label}</div>
              <div className="text-3xl font-bold text-sg-amber">{timeBurnedCount}</div>
              {totalDeceptions > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  {timeBurnRate(timeBurnedCount, totalDeceptions).toFixed(1)}% of decoys delayed the client
                </div>
              )}
            </div>
            <div
              className="bg-sg-card border border-sg-border rounded-xl p-6 transition-all hover:border-green-500/30"
              title={ENGAGEMENT_VOCAB.timeWasted.hint}
            >
              <div className="text-sm text-gray-400 mb-2">{ENGAGEMENT_VOCAB.timeWasted.label}</div>
              <div className="text-3xl font-bold text-sg-green">{safeDuration(totalTimeWasted)}</div>
              {totalTimeWasted > 0 && totalDeceptions > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  {safeDuration(totalTimeWasted / totalDeceptions)} avg per deception
                </div>
              )}
            </div>
            <div
              className="bg-sg-card border border-sg-border rounded-xl p-6 transition-all hover:border-purple-500/30"
              title={ENGAGEMENT_VOCAB.deepCallbacks.hint}
            >
              <div className="text-sm text-gray-400 mb-2">{ENGAGEMENT_VOCAB.deepCallbacks.label}</div>
              <div className="text-3xl font-bold text-purple-400">{deepCallbacks}</div>
              {totalDeceptions > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  Breadcrumb / tracking follow-ups (often 0 for scanners)
                </div>
              )}
            </div>
            <div className="bg-sg-card border border-sg-border rounded-xl p-6 transition-all hover:border-blue-500/30">
              <div className="text-sm text-gray-400 mb-2">Avg Path Depth</div>
              <div className="text-3xl font-bold text-blue-400">{avgPathDepth.toFixed(1)}</div>
              {avgPathDepth > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  {avgPathDepth > 3
                    ? 'Deep path exploration'
                    : avgPathDepth > 1
                      ? 'Moderate path depth'
                      : 'Surface-level paths'}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-gray-800">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-6 py-3 font-medium transition-colors relative ${
            activeTab === 'overview'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Overview
          </div>
        </button>
        <button
          onClick={() => setActiveTab('paths')}
          className={`px-6 py-3 font-medium transition-colors relative ${
            activeTab === 'paths'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <GitBranch className="w-4 h-4" />
            Path Progression
          </div>
        </button>
        <button
          onClick={() => setActiveTab('interactions')}
          className={`px-6 py-3 font-medium transition-colors relative ${
            activeTab === 'interactions'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Interactions
            {interactionsData?.interactions?.length ? (
              <span className="px-1.5 py-0.5 bg-sg-green/20 text-sg-green text-xs rounded">
                {interactionsData.interactions.length}
              </span>
            ) : null}
          </div>
        </button>
      </div>

      {!isConnected ? (
        <EmptyState
          icon={Eye}
          title="Not Connected"
          description="Connect to the control plane in Settings to view deception analytics."
        />
      ) : deceptions.length === 0 && activeTab === 'overview' ? (
        <EmptyState
          icon={Eye}
          title={isLoading ? 'Loading...' : 'No Deceptions Active'}
          description={isLoading ? 'Loading deception data...' : 'Deploy honeypots to begin tracking attacker behavior. Deceptions will appear when served to potential threats.'}
        />
      ) : activeTab === 'overview' ? (
        <div className="bg-sg-card border border-sg-border rounded-xl overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-sg-border">
                <th className="text-left p-3 sm:p-4 text-sm font-medium text-gray-400">Type</th>
                <th className="text-left p-3 sm:p-4 text-sm font-medium text-gray-400">Variant</th>
                <th className="text-left p-3 sm:p-4 text-sm font-medium text-gray-400">Path</th>
                <th
                  className="text-left p-3 sm:p-4 text-sm font-medium text-gray-400"
                  title={ENGAGEMENT_VOCAB.callbackColumn.hint}
                >
                  {ENGAGEMENT_VOCAB.callbackColumn.label}
                </th>
                <th
                  className="text-left p-3 sm:p-4 text-sm font-medium text-gray-400"
                  title={ENGAGEMENT_VOCAB.timeWasted.hint}
                >
                  {ENGAGEMENT_VOCAB.timeWasted.label}
                </th>
                <th className="text-left p-3 sm:p-4 text-sm font-medium text-gray-400">Breadcrumbs</th>
                <th className="text-left p-3 sm:p-4 text-sm font-medium text-gray-400">Created</th>
              </tr>
            </thead>
            <tbody>
              {deceptions.map((d) => (
                <tr key={d.id} className="border-b border-sg-border hover:bg-sg-dark/50 transition-colors">
                  <td className="p-3 sm:p-4"><span className={`px-2 py-1 rounded text-xs font-medium ${d.deception_type === 'honeypot' ? 'bg-sg-amber/10 text-sg-amber' : d.deception_type === 'tarpit' ? 'bg-sg-red/10 text-sg-red' : 'bg-sg-accent/10 text-sg-accent'}`}>{d.deception_type}</span></td>
                  <td className="p-3 sm:p-4 text-sm text-gray-300">{d.variant ?? '—'}</td>
                  <td className="p-3 sm:p-4 text-sm text-gray-300 font-mono break-all max-w-[12rem] sm:max-w-none">{d.path}</td>
                  <td className="p-3 sm:p-4 text-sm">
                    {isDeepCallback(d.engaged) ? (
                      <span className="text-sg-green">{ENGAGEMENT_VOCAB.callbackColumn.yes}</span>
                    ) : (
                      <span className="text-gray-500">{ENGAGEMENT_VOCAB.callbackColumn.no}</span>
                    )}
                  </td>
                  <td className="p-3 sm:p-4 text-sm text-gray-300">{safeDuration(d.time_wasted_ms)}</td>
                  <td className="p-4 text-sm text-gray-300">{safeNumber(d.breadcrumbs_followed)}</td>
                  <td className="p-4 text-sm text-gray-500">{safeDate(d.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : activeTab === 'interactions' ? (
        /* Interactions Timeline Tab */
        <div className="space-y-6">
          {/* Interaction Type Breakdown */}
          {interactionsData?.typeBreakdown && interactionsData.typeBreakdown.length > 0 && (
            <div className="bg-sg-card border border-sg-border rounded-xl p-6">
              <div className="text-sm text-gray-400 mb-4 font-medium">INTERACTION TYPES</div>
              <div className="flex flex-wrap gap-3">
                {interactionsData.typeBreakdown.map((t) => (
                  <div key={t.interaction_type} className="flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-lg">
                    {t.interaction_type === 'pixel_load' && <Eye className="w-4 h-4 text-blue-400" />}
                    {t.interaction_type === 'page_view' && <Eye className="w-4 h-4 text-indigo-400" />}
                    {t.interaction_type === 'form_submit' && <MousePointer className="w-4 h-4 text-green-400" />}
                    {t.interaction_type === 'input_focus' && <Activity className="w-4 h-4 text-yellow-400" />}
                    {t.interaction_type === 'download' && <FileDown className="w-4 h-4 text-purple-400" />}
                    {t.interaction_type === 'api_call' && <Zap className="w-4 h-4 text-orange-400" />}
                    {t.interaction_type === 'breadcrumb_follow' && <GitBranch className="w-4 h-4 text-cyan-400" />}
                    <span className="text-sm text-gray-300">{t.interaction_type.replace(/_/g, ' ')}</span>
                    <span className="px-2 py-0.5 bg-gray-700 rounded text-xs text-white font-medium">{t.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Interactions Timeline */}
          {!interactionsData?.interactions?.length ? (
            <EmptyState
              icon={Zap}
              title={interactionsLoading ? 'Loading...' : 'No Interactions Recorded'}
              description={interactionsLoading ? 'Loading interaction data...' : 'Interactions appear when attackers access honeypots - pixel loads, form submissions, downloads, or API calls.'}
            />
          ) : (
            <div className="bg-sg-card border border-sg-border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-sg-border bg-gray-900">
                    <th className="text-left p-4 text-sm font-medium text-gray-400">Type</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-400">Deception</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-400">Path</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-400">Source</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-400">Threat</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-400">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {interactionsData.interactions.map((i: DeceptionInteraction) => (
                    <tr key={i.id} className="border-b border-sg-border hover:bg-sg-dark/50 transition-colors">
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          i.interaction_type === 'pixel_load' ? 'bg-blue-500/20 text-blue-400' :
                          i.interaction_type === 'form_submit' ? 'bg-green-500/20 text-green-400' :
                          i.interaction_type === 'input_focus' ? 'bg-yellow-500/20 text-yellow-400' :
                          i.interaction_type === 'download' ? 'bg-purple-500/20 text-purple-400' :
                          i.interaction_type === 'api_call' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-cyan-500/20 text-cyan-400'
                        }`}>
                          {i.interaction_type.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          i.deception_type === 'honeypot' ? 'bg-sg-amber/10 text-sg-amber' : 'bg-sg-accent/10 text-sg-accent'
                        }`}>
                          {i.deception_type}
                        </span>
                        {i.variant && <span className="ml-2 text-xs text-gray-500">{i.variant}</span>}
                      </td>
                      <td className="p-4 text-sm text-gray-300 font-mono">{i.path}</td>
                      <td className="p-4">
                        <div className="text-sm text-gray-300">{i.source_ip}</div>
                        {i.source_country && <div className="text-xs text-gray-500">{i.source_country}</div>}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          i.threat_level === 'critical' ? 'bg-red-500/20 text-red-400' :
                          i.threat_level === 'high' ? 'bg-orange-500/20 text-orange-400' :
                          i.threat_level === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {i.threat_level}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-gray-500">{safeDate(i.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* Path Progression Tab */
        <div className="space-y-4">
          {sessionPaths.size === 0 ? (
            <EmptyState
              icon={GitBranch}
              title="No Attack Paths Detected"
              description="Attack paths appear when attackers follow embedded breadcrumbs across multiple deception layers."
            />
          ) : (
            Array.from(sessionPaths.entries()).map(([sessionId, paths]) => {
              const totalSessionTime = paths.reduce((sum, p) => sum + p.timeWasted, 0);
              const maxDepth = Math.max(...paths.map(p => p.depth));
              const displaySessionId = sessionId && sessionId.length > 20 
                ? `${sessionId.substring(0, 20)}...` 
                : sessionId || 'Unknown';
              
              return (
                <div key={sessionId || Math.random()} className="bg-sg-card border border-sg-border rounded-xl overflow-hidden">
                  {/* Session Header */}
                  <div className="p-4 border-b border-sg-border bg-sg-dark/50 relative">
                    {/* Color rail */}
                    <div className="absolute left-0 top-0 h-full w-1" style={{ background: sessionHue(sessionId || '') }} />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => toggleExpanded(sessionId || '')}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleExpanded(sessionId || '');
                            }
                          }}
                          aria-label={`${expanded[sessionId || ''] ? 'Collapse' : 'Expand'} session details`}
                          className="text-gray-400 hover:text-white transition-all hover:scale-110"
                        >
                          {expanded[sessionId || ''] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <Target className="w-5 h-5" style={{ color: sessionHue(sessionId || '') }} />
                        <span className="font-mono text-sm text-gray-300">{displaySessionId}</span>
                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded">
                          {paths.length} deceptions
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-400">
                          <TrendingUp className="w-4 h-4" />
                          <span>Max depth {maxDepth}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                          <Clock className="w-4 h-4" />
                          <span>{safeDuration(totalSessionTime)} wasted</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Collapsed Summary */}
                  {!(expanded[sessionId || ''] ?? false) && (
                    <div className="p-4 text-sm text-gray-400">
                      <div className="flex items-center gap-4">
                        <span>{paths.length} deceptions</span>
                        <span>•</span>
                        <span>Depth: {maxDepth}</span>
                        <span>•</span>
                        <span>{safeDuration(totalSessionTime)} wasted</span>
                        <span>•</span>
                        <span>
                          {paths.filter((p) => p.deepCallback).length}{' '}
                          {ENGAGEMENT_VOCAB.deepCallbacks.short.toLowerCase()}
                        </span>
                        <span>•</span>
                        <span>
                          {paths.filter((p) => p.timeBurned).length}{' '}
                          {ENGAGEMENT_VOCAB.timeBurned.short.toLowerCase()}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Path Tree */}
                  {expanded[sessionId || ''] && (
                  <div className="p-6 bg-gray-950 rounded-lg">
                    <div className="font-mono text-sm">
                      {paths.map((node, idx) => {
                        const isCallback = node.deepCallback;
                        const burned = node.timeBurned;
                        const isLast = idx === paths.length - 1;
                        const nextNode = idx < paths.length - 1 ? paths[idx + 1] : null;
                        const isLastAtDepth = !nextNode || nextNode.depth < node.depth;
                        const hasChildren = nextNode && nextNode.depth > node.depth;
                        
                        // Build tree prefix (like git tree)
                        let treePrefix = '';
                        for (let i = 0; i < node.depth; i++) {
                          if (i === node.depth - 1) {
                            treePrefix += isLastAtDepth ? '└── ' : '├── ';
                          } else {
                            treePrefix += '│   ';
                          }
                        }

                        const badge = isCallback
                          ? {
                              text: `✓ ${ENGAGEMENT_VOCAB.pathNode.callback}`,
                              cls: 'bg-green-500/20 text-green-400',
                            }
                          : burned
                            ? {
                                text: ENGAGEMENT_VOCAB.pathNode.timeBurned,
                                cls: 'bg-amber-500/20 text-amber-400',
                              }
                            : {
                                text: ENGAGEMENT_VOCAB.pathNode.served,
                                cls: 'bg-gray-600/20 text-gray-400',
                              };
                        
                        return (
                          <div key={idx} className="py-1">
                            <div className="flex items-start gap-2 hover:bg-gray-900/50 p-2 rounded transition-colors">
                              {/* Tree structure */}
                              <div className="flex-shrink-0">
                                <span className="text-gray-600">{treePrefix}</span>
                                <span className={`inline-block w-3 h-3 rounded-full ${
                                  node.depth === 0
                                    ? 'bg-blue-500'
                                    : isCallback
                                      ? 'bg-green-500'
                                      : burned
                                        ? 'bg-amber-500'
                                      : 'bg-gray-500'
                                }`} />
                              </div>
                              
                              {/* Path and info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <code className={`text-base font-semibold ${
                                    node.depth === 0 ? 'text-blue-400' :
                                    node.depth === 1 ? 'text-yellow-400' :
                                    node.depth === 2 ? 'text-orange-400' :
                                    node.depth === 3 ? 'text-red-400' :
                                    'text-purple-400'
                                  }`}>
                                    {node.path}
                                  </code>
                                  
                                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${badge.cls}`}>
                                    {badge.text}
                                  </span>
                                  
                                  {node.variant && (
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${variantColors[node.variant] ?? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'}`}>
                                      {node.variant}
                                    </span>
                                  )}
                                  
                                  {node.timeWasted > 0 && (
                                    <span className="px-2 py-0.5 bg-green-600 text-white rounded text-xs font-bold">
                                      +{(node.timeWasted / 1000).toFixed(1)}s
                                    </span>
                                  )}
                                  
                                  <span className="text-xs text-gray-500">
                                    {new Date(node.createdAt).toLocaleTimeString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
